import { AppSettingsService } from '../appSettings/AppSettingsService';
import { LazyLoaderService } from '../lazy-loader/lazy-loader.service';
import { IInfoboxCustomButton, IMapPin } from '@caslink/common-client/ng-app/bing-map/typings/models';
import { IMapService } from '@caslink/common-client/ng-app/bing-map/typings/services';
import { Injectable } from '@angular/core';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { EventTypes } from '../../src';


@Injectable({
    providedIn: 'root'
})
export class BingMapService implements IMapService {
    public static $name: string = 'clBingMapService';

    // tslint:disable-next-line:max-line-length
    constructor(
        private lazyLoaderService: LazyLoaderService,
        private appSettingsService: AppSettingsService,
        private commonBroadcastService: CommonBroadcastService,
    ) {
    }

    private map: Microsoft.Maps.Map;
    private infobox: Microsoft.Maps.Infobox;
    private customBtn: IInfoboxCustomButton;
    private spiderManager: SpiderClusterManager;
    private pinColor;
    private pushpins: IMapPin[];

    init(
        container: HTMLElement,
        pins?: IMapPin[],
        customBtn?: any
    ) {
        const url = `https://www.bing.com/api/maps/mapcontrol?callback=mapScriptLoadCallback&key=${this.appSettingsService.getMetadata().bingMapsKey}`;
        if (this.lazyLoaderService.load(url)) {
            this.initMap(container, pins);
        } else {
            window['mapScriptLoadCallback'] = () => {
                this.initMap(container, pins);
            };
        }
        this.customBtn = customBtn;

        const observer = new MutationObserver((mutationsList, srcObserver) => {
            if (mutationsList.some(item =>
                (item.attributeName === 'height' || item.attributeName === 'width') &&
                item.target['id'] === 'labelCanvasId')) {
                this.showPushpins();
                srcObserver.disconnect();
            }
        });

        observer.observe(container, { attributes: true, childList: true, subtree: true });
    }

    public updatePins(pins: IMapPin[]) {
        if (pins) {
            this.pushpins = pins.filter(pin => pin.latitude != null && pin.longitude != null);
        }
        this.showPushpins();
    }

    public dispose() {
        if (this.spiderManager) {
            this.spiderManager.dispose();
        }

        if (this.map) {
            this.map.dispose();
        }
    }

    private showPushpins() {
        if (this.map && this.pushpins) {
            if (this.map.layers && this.map.layers.length > 0) {
                this.map.layers.clear();
            }
            const mappedPins = this.mapPushpins(this.pushpins);
            if (this.spiderManager) {
                this.spiderManager = this.initSpiderManager(mappedPins);
            } else {
                Microsoft.Maps.loadModule('SpiderClusterManager', () => {
                    this.spiderManager = this.initSpiderManager(mappedPins);
                });
            }
            this.centerMap(mappedPins);
        }
    }

    private initSpiderManager(pins: Microsoft.Maps.Pushpin[]) {
        return new SpiderClusterManager(this.map, pins, {
            pinColor: this.pinColor,
            pinSelected: (pin, cluster) => {
                if (cluster) {
                    this.setInfobox(this.infobox, pin, cluster.getLocation());
                } else {
                    this.setInfobox(this.infobox, pin);
                }

                this.commonBroadcastService.broadcast(EventTypes.OnMapPinClick, pin.metadata);
            },
            pinUnselected: () => {
                this.infobox.setMap(null);
            }
        });
    }

    private setInfobox(
        infobox: Microsoft.Maps.Infobox,
        pushpin: Microsoft.Maps.Pushpin,
        location: Microsoft.Maps.Location = null) {

        if (pushpin.metadata) {
            const infoboxHtmlTemplate = this.getInfoboxHtmlTemplate(pushpin.metadata.description, pushpin.metadata.title);
            infobox.setOptions({
                htmlContent: infoboxHtmlTemplate,
                location: location ? location : pushpin.getLocation(),
                visible: true,
            });
            infobox.setMap(this.map);

            if (this.customBtn) {
                window['onInfoboxButtonClick'] = () => {
                    this.customBtn.onClick(pushpin.metadata);
                };
            }
        }
    }

    private getInfoboxHtmlTemplate(description: string, title: string,) {
        const customBtn = this.getCustomButtonTemplate(this.customBtn?.label);

        return `<div class="Infobox">
                    <a href="javascript:closeInfobox()"
                    class="custom-infobox-close-button">
                        <span class="material-icons">
                        close</span>
                    </a>
                    <div class="infobox-body">
                        <div class="infobox-title">${title}</div>
                        <div class="infobox-info">
                            <div>${description}</div>
                        </div>
                    </div>
                    ${customBtn}
                </div>`;
    }

    private getCustomButtonTemplate(label?: string) {
        if (label) {
            return `<div class='infobox-btn-wrapper'>
                            <button class='md-button md-autofocus md-casLight-theme md-ink-ripple md-accent md-raised' 
                                    onclick='onInfoboxButtonClick()'>${label}</button>
                    </div>`;
        }

        return '';
    }

    private centerMap(pins: Microsoft.Maps.Pushpin[]) {
        if (pins == null || pins.length === 0) {
            return;
        }

        this.map.setView({
            bounds: Microsoft.Maps.LocationRect.fromShapes(pins),
            padding: 100,
        });
    }

    private initMap(container: HTMLElement, pins?: IMapPin[]) {
        if (window['Microsoft']['Maps']['Map']) {
            let centerLoc = null;
            if (pins?.length > 0) {
                centerLoc = Microsoft.Maps.LocationRect.fromShapes(this.mapPushpins(pins)).center;
            }

            this.map = new Microsoft.Maps.Map(container, {
                allowInfoboxOverflow: true,
                center: centerLoc,
                showDashboard: false,
            });

            this.infobox = new Microsoft.Maps.Infobox(this.map.getCenter(), {
                visible: false
            });

            // tslint:disable-next-line: no-magic-numbers
            this.pinColor = new Microsoft.Maps.Color(0, 54, 128, 61);

            window['closeInfobox'] = () => {
                this.infobox.setOptions({ visible: false });
            };

            Microsoft.Maps.Events.addHandler(this.infobox, 'infoboxChanged', (e) => { this.onInfoboxClicked(e); });
            this.infobox.setMap(this.map);

            Microsoft.Maps.registerModule(
                'SpiderClusterManager',
                'dist/spider-cluster-manager.min.js'
            );

            this.updatePins(pins);
        }
    }

    private onInfoboxClicked(e) {
        const infobox = e.target as Microsoft.Maps.Infobox;
        // tslint:disable-next-line: no-boolean-literal-compare
        if (infobox && infobox.getVisible() === false && infobox.getOptions().visible === false) {

            this.commonBroadcastService.broadcast(EventTypes.OnInfoboxClosed);
        }
    }

    private mapPushpins(filteredPins: IMapPin[]) {
        const mappedPins: Microsoft.Maps.Pushpin[] = [];
        for (const pin of filteredPins) {
            const newPin = new Microsoft.Maps.Pushpin(
                new Microsoft.Maps.Location(pin.latitude, pin.longitude), {
                color: this.pinColor
            });
            newPin.metadata = {
                description: pin.detail,
                locationId: pin.locationId,
                title: pin.location
            };
            mappedPins.push(newPin);
        }
        return mappedPins;
    }
}
